import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { DragDropContext } from "@hello-pangea/dnd";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Menu, MenuItem, Paper, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { useThemeContext, DENSITY_CONFIG } from "../../context/DarkMode";
import { useFloatingChat } from "../../context/FloatingChat";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useQueueFilterState, { parseStoredQueueFilter } from "../../hooks/useQueueFilterState";
import { useUiPrefs } from "../../context/UiPrefs";
import { TURNOS_PROFILES } from "../../constants/turnosProfiles";
import useCrmBoard, { useCrmStatus } from "../../hooks/useCrmBoard";
import ContactStateModal from "../ContactStateModal";
import NewTicketModal from "../NewTicketModal";
import TransferTicketModal from "../TransferTicketModal";
import getContactDisplayName from "../../helpers/contactDisplayName";
import BoardColumn from "./BoardColumn";
import BoardFilters from "./BoardFilters";
import { contactIdFromDraggable, findCard, keyToStateId, moveCard } from "./boardState";

// feat_395 — Tablero CRM (frontend del feat_394).
//
// Una columna por estado de contacto (+ "Sin estado" primera, + inactivas con
// contactos en solo lectura). Arrastrar una ficha cambia el estado del contacto
// con PUT /contacts/:id/state { source:"board", autoClose:true }.
//
// Decisiones (ver CLAUDE.md › Tablero CRM frontend):
//  - Reordenar DENTRO de una columna no existe: el orden es por actividad. La
//    columna de origen se marca isDropDisabled durante el drag → soltar ahí es
//    "sin destino" y la librería devuelve la ficha a su lugar sin parpadeo.
//  - Soltar en "Sin estado" = stateId null (el backend lo acepta). Con etapa de
//    entrada (feat_397) "Sin estado" no acepta drop: un contacto con chat sin
//    estado se muestra en la entrada, así que soltarlo ahí no tendría efecto.
//  - Soltar en Perdido (systemId 5) abre el modal de motivos; cancelar revierte
//    sin llamar al backend.

const FILTER_STORAGE_PREFIX = "waia:crmBoardFilter";
const EMPTY_FILTERS = { search: "", userSelection: null, dateFrom: "", dateTo: "" };

// feat_396 — los campos del filtro de colas viven en la MISMA clave del Funnel
// (independiente de la bandeja). Un objeto guardado antes de feat_396 no los
// tiene → `queue: null` → el hook cae al default (= lo que el usuario ve en Chats).
const QUEUE_KEYS = ["selectedQueueIds", "showAllTickets", "showOrphans", "prevShowOrphans", "prevSelectedQueueIds"];

const readStoredFilters = (key, userQueues) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...EMPTY_FILTERS, queue: null };
    const p = JSON.parse(raw);
    const sel = p && p.userSelection;
    const validSel =
      sel && Array.isArray(sel.ids) && sel.ids.every(n => Number.isInteger(n))
        ? { ids: sel.ids, unassigned: !!sel.unassigned }
        : null;
    const day = v => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "");
    const hasQueue = p && (Array.isArray(p.selectedQueueIds) || typeof p.showAllTickets === "boolean");
    return {
      search: typeof p.search === "string" ? p.search.slice(0, 100) : "",
      userSelection: validSel,
      dateFrom: day(p.dateFrom),
      dateTo: day(p.dateTo),
      queue: hasQueue ? parseStoredQueueFilter(p, userQueues) : null,
    };
  } catch (_) {
    return { ...EMPTY_FILTERS, queue: null };
  }
};

const isDark = theme => theme.palette.type === "dark";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5, 2, 1),
    background: isDark(theme) ? "#0b141a" : "#efeae2",
  },
  titleRow: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  title: { fontWeight: 700 },
  board: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  center: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: theme.spacing(3) },
  emptyBox: {
    maxWidth: theme.spacing(60),
    padding: theme.spacing(3),
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  emptyIcon: { fontSize: theme.spacing(6), color: theme.palette.text.secondary },
}));

const CRMBoard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);
  const { cfg } = useThemeContext();
  const { openChat } = useFloatingChat();
  const isAdmin = user?.profile === "admin";
  const avatarSize = Math.round((cfg || DENSITY_CONFIG.normal).bigAvatar * 0.8);

  const settingValue = getSetting("crmBoardEnabled");
  const { status, loading: statusLoading, available } = useCrmStatus(settingValue);

  // ── Filtros (persistidos por empresa/usuario, molde de feat_326) ──────────
  const storageKey = `${FILTER_STORAGE_PREFIX}:${user?.companyId || 0}:${user?.id || 0}`;
  const initialStored = useMemo(
    () => readStoredFilters(storageKey, user?.queues),
    // se lee una sola vez al montar (mismo criterio que la bandeja)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [filtersUi, setFiltersUi] = useState(() => {
    const { queue, ...rest } = initialStored;
    return rest;
  });

  // feat_396 — filtro de colas propio del Funnel (mismo componente y misma
  // lógica que la bandeja, otra clave de localStorage).
  const {
    showAllTickets,
    selectedQueueIds,
    setSelectedQueueIds,
    showOrphans,
    prevShowOrphans,
    prevSelectedQueueIds,
    handleToggleShowAll,
    toggleShowOrphans,
    resetQueueFilter,
  } = useQueueFilterState({ user: user || {}, persisted: initialStored.queue });
  const { hideChannels } = useUiPrefs();

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...filtersUi,
          selectedQueueIds,
          showAllTickets,
          showOrphans,
          prevShowOrphans,
          prevSelectedQueueIds,
        })
      );
    } catch (_) {}
  }, [storageKey, filtersUi, selectedQueueIds, showAllTickets, showOrphans, prevShowOrphans, prevSelectedQueueIds]);

  // "Sin asignar" (tickets sin cola) sólo existe para admin o con
  // canSeeUnassigned; profesional/agendador nunca (privacidad TurnosIA, mismo
  // corte que el backend).
  const canSeeOrphans =
    isAdmin || (!!user?.canSeeUnassigned && !TURNOS_PROFILES.includes(user?.profile));
  const myQueueIds = (user?.queues || []).map(q => q.id);
  const effShowAll = isAdmin && showAllTickets;
  const effOrphans = canSeeOrphans && showOrphans;
  const selectedMine = myQueueIds.filter(id => selectedQueueIds.includes(id));
  const queueStrict = isAdmin
    ? !effShowAll
    : selectedMine.length < myQueueIds.length || (canSeeOrphans && !showOrphans);

  const debouncedSearch = useDebouncedValue(filtersUi.search, 300);
  // El filtro de operador sólo existe para admin (GET /users es de admin).
  const effectiveSelection = isAdmin ? filtersUi.userSelection : null;
  const filterKey = JSON.stringify({
    q: effShowAll
      ? { a: true }
      : { i: isAdmin ? [...selectedQueueIds].sort((a, b) => a - b) : selectedMine, o: effOrphans, s: queueStrict },
    h: !!hideChannels,
    u: effectiveSelection,
    f: filtersUi.dateFrom,
    t: filtersUi.dateTo,
    s: debouncedSearch.trim(),
  });
  const filters = useMemo(() => {
    const p = JSON.parse(filterKey);
    return {
      userIds: p.u ? p.u.ids : [],
      includeUnassigned: p.u ? p.u.unassigned : false,
      lastContactFrom: p.f,
      lastContactTo: p.t,
      searchParam: p.s,
      queue: p.q.a
        ? { showAll: true, queueIds: [], showOrphans: true, strict: false }
        : { showAll: false, queueIds: p.q.i, showOrphans: p.q.o, strict: p.q.s },
      hideChannels: p.h,
    };
  }, [filterKey]);

  const { columns, columnsRef, setColumns, loading, loadingMore, error, reload, loadMore, changeState, hasFilters, entryStateId } =
    useCrmBoard({ enabled: available, filters, filterKey, isAdmin });

  // Usuarios para el filtro (solo admin), paginado como TicketsManager.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!isAdmin || !available) return undefined;
    let cancelled = false;
    (async () => {
      let pageNumber = 1;
      let collected = [];
      let hasMore = true;
      while (hasMore && pageNumber <= 10) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/users", { params: { pageNumber } });
        collected = [...collected, ...(data.users || [])];
        hasMore = !!data.hasMore;
        pageNumber += 1;
      }
      if (!cancelled) setUsers(collected.filter(u => !u.isSuperAdmin));
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdmin, available]);

  // Estados (para el modal de Perdido), motivos y cierre automático.
  const [states, setStates] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [closeSettings, setCloseSettings] = useState({ closeOnGained: true, closeOnLost: true });
  useEffect(() => {
    if (!available) return;
    Promise.all([api.get("/contact-states"), api.get("/lost-reasons"), api.get("/companies/close-settings")])
      .then(([s, r, c]) => {
        setStates(s.data || []);
        setLostReasons(r.data || []);
        setCloseSettings({
          closeOnGained: c.data?.closeOnGained !== false,
          closeOnLost: c.data?.closeOnLost !== false,
        });
      })
      .catch(() => {});
  }, [available]);

  // Reloj para los tiempos relativos de las fichas (1 tick/min).
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const [draggingFrom, setDraggingFrom] = useState(null);
  const [lostPending, setLostPending] = useState(null); // { contactId, fromKey, fromIndex, toKey }
  const lostDone = useRef(false);

  const revert = useCallback(
    move => setColumns(prev => moveCard(prev, move.contactId, move.fromKey, move.fromIndex) || prev),
    [setColumns]
  );

  const persistMove = useCallback(
    async (move, extra = {}) => {
      const target = columnsRef.current.find(c => c.key === move.toKey);
      const card = move.card;
      try {
        await changeState(move.contactId, { stateId: keyToStateId(move.toKey), ...extra });
        const sys = target?.systemId;
        const hadOpen = (card?.openTickets || 0) > 0;
        if (hadOpen && ((sys === 4 && closeSettings.closeOnGained) || (sys === 5 && closeSettings.closeOnLost))) {
          toast.info(i18n.t("crmBoard.closedAuto"));
        }
        return true;
      } catch (err) {
        revert(move);
        if (err?.response?.data?.message || err?.response?.data?.error) toastError(err);
        else toast.error(i18n.t("crmBoard.moveError"));
        return false;
      }
    },
    [changeState, closeSettings, columnsRef, revert]
  );

  const onDragStart = useCallback(start => setDraggingFrom(start.source.droppableId), []);

  const onDragEnd = useCallback(
    result => {
      setDraggingFrom(null);
      const { source, destination, draggableId } = result;
      if (!destination || destination.droppableId === source.droppableId) return;
      const contactId = contactIdFromDraggable(draggableId);
      if (!contactId) return;
      const target = columnsRef.current.find(c => c.key === destination.droppableId);
      if (!target || target.active === false) return;
      if (target.stateId === null && entryStateId) return;
      const found = findCard(columnsRef.current, contactId);
      const move = {
        contactId,
        card: found?.card,
        fromKey: source.droppableId,
        fromIndex: source.index,
        toKey: destination.droppableId,
      };
      // Optimista: la ficha queda en destino mientras se guarda (o mientras el
      // modal de motivos está abierto).
      setColumns(prev => moveCard(prev, contactId, move.toKey, destination.index) || prev);
      if (target.systemId === 5) {
        lostDone.current = false;
        setLostPending(move);
        return;
      }
      persistMove(move);
    },
    [columnsRef, entryStateId, persistMove, setColumns]
  );

  // ContactStateModal llama changeState(stateId, lostReasonId, { lostReasonText }).
  const modalChangeState = useCallback(
    async (stateId, lostReasonId, opts = {}) => {
      const move = lostPending;
      if (!move) return null;
      // No se tira error si falla: persistMove ya revirtió la ficha y mostró el
      // toast; tirar haría que el modal sume un segundo toast genérico.
      await persistMove(move, { lostReasonId, lostReasonText: opts.lostReasonText });
      lostDone.current = true;
      return states.find(s => s.id === stateId) || null;
    },
    [lostPending, persistMove, states]
  );

  const handleLostClose = useCallback(() => {
    // Cancelar sin confirmar → la ficha vuelve a su columna; no salió ningún PUT.
    if (lostPending && !lostDone.current) revert(lostPending);
    setLostPending(null);
  }, [lostPending, revert]);

  // ── Abrir chat ────────────────────────────────────────────────────────────
  const [newTicketContact, setNewTicketContact] = useState(null);
  const handleOpenCard = useCallback(
    card => {
      const ticketId = card?.ticket?.id;
      if (ticketId) {
        openChat(ticketId, getContactDisplayName(card.contact, { fallback: String(ticketId) }));
        return;
      }
      setNewTicketContact(card.contact);
    },
    [openChat]
  );

  // ── Menú de la ficha: Abrir en el chat / Transferir (Funnel tanda 4) ─────
  const [cardMenu, setCardMenu] = useState(null); // { card, anchor }
  const [transferTicket, setTransferTicket] = useState(null);
  const handleCardMenu = useCallback((card, anchor) => setCardMenu({ card, anchor }), []);

  // Tras transferir (desde la ficha o desde el flotante) el ticket queda
  // pending con otro dueño: el backend decide si la ficha sigue en MI scope
  // (feat_396) → refetch, no reload de página.
  useEffect(() => {
    const onTransferred = () => reload();
    window.addEventListener("waia:ticket-transferred", onTransferred);
    return () => window.removeEventListener("waia:ticket-transferred", onTransferred);
  }, [reload]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (statusLoading && !status) {
    return (
      <div className={classes.root}>
        <div className={classes.center}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!available) {
    const needsStates = !!status?.enabled && !status?.hasActiveStates;
    return (
      <div className={classes.root}>
        <div className={classes.center}>
          <Paper className={classes.emptyBox} variant="outlined" data-crm-empty={needsStates ? "needs-states" : "disabled"}>
            <ViewColumnOutlinedIcon className={classes.emptyIcon} />
            <Typography variant="h6">
              {i18n.t(needsStates ? "crmBoard.needsStatesTitle" : "crmBoard.disabledTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {i18n.t(needsStates ? "crmBoard.needsStatesText" : "crmBoard.disabledText")}
            </Typography>
            {isAdmin && (
              <Button variant="contained" color="primary" onClick={() => history.push("/contact-states-admin")}>
                {i18n.t("crmBoard.goToStates")}
              </Button>
            )}
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.titleRow}>
        <ViewColumnOutlinedIcon />
        <Typography variant="h6" component="h1" className={classes.title}>
          {i18n.t("crmBoard.title")}
        </Typography>
      </div>
      <BoardFilters
        search={filtersUi.search}
        onSearch={v => setFiltersUi(f => ({ ...f, search: v }))}
        users={isAdmin ? users : []}
        userSelection={effectiveSelection}
        onUserSelection={sel => setFiltersUi(f => ({ ...f, userSelection: sel }))}
        myUserId={user?.id}
        dateFrom={filtersUi.dateFrom}
        dateTo={filtersUi.dateTo}
        onDateFrom={v => setFiltersUi(f => ({ ...f, dateFrom: v }))}
        onDateTo={v => setFiltersUi(f => ({ ...f, dateTo: v }))}
        showClear={hasFilters || !!filtersUi.search}
        onClear={() => {
          setFiltersUi(EMPTY_FILTERS);
          resetQueueFilter();
        }}
        userQueues={user?.queues || []}
        selectedQueueIds={selectedQueueIds}
        onQueueChange={setSelectedQueueIds}
        showAllTickets={showAllTickets}
        showOrphans={showOrphans}
        canSeeOrphans={canSeeOrphans}
        isAdmin={isAdmin}
        onToggleShowAll={handleToggleShowAll}
        onToggleShowOrphans={toggleShowOrphans}
        onRefresh={reload}
      />
      {entryStateId === null && (
        <Alert
          severity="info"
          variant="outlined"
          style={{ marginBottom: 8, padding: "0 12px" }}
          data-crm-no-entry
          action={
            isAdmin ? (
              <Button color="primary" size="small" onClick={() => history.push("/contact-states-admin")}>
                {i18n.t("crmBoard.noEntryAction")}
              </Button>
            ) : undefined
          }
        >
          {i18n.t(isAdmin ? "crmBoard.noEntryText" : "crmBoard.noEntryTextUser")}
        </Alert>
      )}
      {error && columns.length === 0 ? (
        <div className={classes.center}>
          <Paper className={classes.emptyBox} variant="outlined">
            <Typography variant="body2">{i18n.t("crmBoard.loadError")}</Typography>
            <Button variant="outlined" onClick={reload}>
              {i18n.t("crmBoard.refresh")}
            </Button>
          </Paper>
        </div>
      ) : (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className={classes.board} data-crm-board>
            {columns.map(col => (
              <BoardColumn
                key={col.key}
                column={col}
                dropDisabled={draggingFrom === col.key || (col.stateId === null && !!entryStateId)}
                loading={loading}
                loadingMore={loadingMore[col.key]}
                onLoadMore={loadMore}
                avatarSize={avatarSize}
                now={now}
                onOpenCard={handleOpenCard}
                onCardMenu={handleCardMenu}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      <ContactStateModal
        open={!!lostPending}
        onClose={handleLostClose}
        contactId={lostPending?.contactId}
        currentState={null}
        states={states}
        lostReasons={lostReasons}
        loading={false}
        changeState={modalChangeState}
        onStateChanged={() => {}}
        forceStep="lost"
        hideBack
      />
      {(() => {
        const t = cardMenu?.card?.ticket;
        const hasTicket = !!t?.id;
        const transferable = hasTicket && (t.status === "open" || t.status === "pending");
        return (
          <Menu
            anchorEl={cardMenu?.anchor || null}
            open={!!cardMenu}
            onClose={() => setCardMenu(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              data-card-open-in-chat
              disabled={!hasTicket}
              onClick={() => {
                setCardMenu(null);
                history.push(`/tickets/${t.id}`);
              }}
            >
              {i18n.t("crmBoard.openInChat")}
            </MenuItem>
            <MenuItem
              data-card-transfer
              disabled={!transferable}
              onClick={() => {
                setCardMenu(null);
                setTransferTicket(t);
              }}
            >
              {i18n.t("crmBoard.transfer")}
            </MenuItem>
            {hasTicket && !transferable && (
              <MenuItem disabled style={{ whiteSpace: "normal", maxWidth: 260, fontSize: "0.8em" }}>
                {i18n.t("crmBoard.transferClosedHint")}
              </MenuItem>
            )}
            {!hasTicket && (
              <MenuItem disabled style={{ whiteSpace: "normal", maxWidth: 260, fontSize: "0.8em" }}>
                {i18n.t("crmBoard.noTicket")}
              </MenuItem>
            )}
          </Menu>
        );
      })()}
      {transferTicket && (
        <TransferTicketModal
          modalOpen
          onClose={() => setTransferTicket(null)}
          ticketid={transferTicket.id}
          ticketWhatsappId={transferTicket.whatsappId}
          onTransferred={() => {
            setTransferTicket(null);
            reload();
          }}
        />
      )}
      <NewTicketModal
        modalOpen={!!newTicketContact}
        initialContact={newTicketContact}
        onClose={() => setNewTicketContact(null)}
      />
    </div>
  );
};

export default CRMBoard;
